import { Text } from "@kvix/ui";
import { Button, makeStyles } from "@material-ui/core";
import classNames from "classnames";
import { motion } from "framer-motion";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useHistory } from "react-router-dom";
import { AnimatedBackground } from "./AnimatedBackground";

const useStyles = makeStyles((theme) => ({
  container: {
    position: "absolute",
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    margin: "auto",
    padding: 40,
    background: theme.palette.background.default,
    height: 678,
    width: 510,
    boxShadow: "0 0 50px rgba(0,0,0,0.25)",
    overflow: "auto",
    maxHeight: "100%",

    [theme.breakpoints.down("xs")]: {
      height: "85%",
      width: "90%",
      boxShadow: "none",
      top: 56,
      padding: "48px 24px",
    },
  },
  title: {
    whiteSpace: "pre-line",
    marginBottom: theme.spacing(5),
  },
  text: {
    fontWeight: 500,
    lineHeight: 1.6,
    fontSize: 16,

    "&.spacing": {
      marginBottom: theme.spacing(4),
    },

    "&.link": {
      color: theme.palette.primary.main,
    },
  },
  button: {
    float: "right",

    [theme.breakpoints.down("xs")]: {
      float: "none",
      width: "100%",
    },
  },
}));

export default () => {
  const { push } = useHistory();
  const [open, setOpen] = useState(false);
  const openTimeout = useRef<number>();
  const { t, ready } = useTranslation("successfulRegistration");
  const classes = useStyles();
  const noOfAnimationSteps = 7;
  const popupDelay = 0.3 * noOfAnimationSteps * 1000;

  useEffect(() => {
    openTimeout.current = window.setTimeout(() => {
      setOpen(true);
    }, popupDelay);

    return () => {
      clearTimeout(openTimeout.current);
    };
  }, [popupDelay]);

  if (!ready) {
    return null;
  }

  return (
    <AnimatedBackground animationSteps={noOfAnimationSteps} modalOpen={open}>
      {open && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className={classes.container}
        >
          <Text variant="h3" className={classes.title}>
            {t("title")}
          </Text>
          <Text
            variant="body2"
            className={classNames(classes.text, { spacing: true })}
            gutterBottom
          >
            {t("text1")}
          </Text>
          <Text variant="h5" gutterBottom>
            {t("subtitle1")}
          </Text>
          <Text variant="body2" className={classes.text} gutterBottom>
            {t("text2")}
          </Text>
          <Text
            variant="body2"
            className={classNames(classes.text, {
              link: true,
              spacing: true,
            })}
            gutterBottom
          >
            <a
              href="https://www.facebook.com/groups/270805361376531"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("buttonText1")}
            </a>
          </Text>
          <Text variant="h5" gutterBottom>
            {t("subtitle2")}
          </Text>
          <Text variant="body2" className={classes.text} gutterBottom>
            {t("text3")}
          </Text>
          <Text
            variant="body2"
            className={classNames(classes.text, {
              link: true,
              spacing: true,
            })}
            gutterBottom
          >
            <Link to="/sessions">{t("buttonText2")}</Link>
          </Text>
          <Button
            variant="contained"
            color="primary"
            className={classes.button}
            onClick={() => push("/streamer-admin")}
          >
            {t("buttonText3")}
          </Button>
        </motion.div>
      )}
    </AnimatedBackground>
  );
};
